// src/config/api/apiConfig.js

// Configuration
// Base URL
export const BASE_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:3001'
  : process.env.REACT_APP_API_URL;

export const API_CONFIG = {

  // API endpoints - updated to match your server routes
  ENDPOINTS: {
    SUBSCRIPTIONS: '/stripe/subscriptions',
    PRICES: '/stripe/prices',
    CUSTOMER: '/stripe/customers',
    CHECKOUT: '/stripe/checkout/sessions',
    PORTAL: '/stripe/customer-portal',
    CHAT: '/api/chat/bypass',
  },

  // Request headers
  getHeaders: token => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }),
};

// Centralized API request handler with debug logging
async function apiRequest(endpoint, options = {}) {
  const url = `${BASE_URL}${endpoint}`;

  // Debug logging
  console.log('Making API request:', {
    url,
    method: options.method || 'GET',
    headers: API_CONFIG.getHeaders(options.token),
    body: options.body ? JSON.parse(options.body) : undefined,
  });

  try {
    const response = await fetch(url, {
      method: options.method || 'GET',
      headers: {
        ...API_CONFIG.getHeaders(options.token),
        ...options.headers,
      },
      ...(options.body && { body: options.body }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        error: `HTTP error! status: ${response.status}`,
      }));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('API response:', data); // Debug logging
    return data;
  } catch (error) {
    console.error('API Request failed:', {
      url,
      error: error.message,
      stack: error.stack,
    });
    throw error;
  }
}

// API methods
export const api = {
  async getCustomer(userId, token) {
    const response = await fetch(`${BASE_URL}/api/stripe/customer/${userId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },

  async getSubscriptions(userId, token) {
    const customerData = await this.getCustomer(userId, token);
    const response = await fetch(`${BASE_URL}subscriptions/${customerData.stripeCustomerId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },

  // Fetch available prices
  getPrices: async token => {
    return await apiRequest(API_CONFIG.ENDPOINTS.PRICES, {
      token,
    });
  },

  checkFeatureAccess: async (userId, feature, token) => {
    const response = await fetch(`${BASE_URL}/subscriptions/${userId}/features/${feature}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },

  // Create or get customer
  getOrCreateCustomer: async (userData, token) => {
    return await apiRequest(API_CONFIG.ENDPOINTS.CUSTOMER, {
      method: 'POST',
      token,
      body: JSON.stringify(userData),
    });
  },

  // Create checkout session
  createCheckoutSession: async (sessionData, token) => {
    return await apiRequest(API_CONFIG.ENDPOINTS.CHECKOUT, {
      method: 'POST',
      token,
      body: JSON.stringify(sessionData),
    });
  },

  // Create portal session
  createPortalSession: async (sessionData, token) => {
    return await apiRequest(API_CONFIG.ENDPOINTS.PORTAL, {
      method: 'POST',
      token,
      body: JSON.stringify(sessionData),
    });
  },
};

export default API_CONFIG;
